// Finish and Next action footer shared by every contact-scoped screen.
//
// Disabled state and labels follow the approved HTML. The precondition
// for the enabled state is set by each parent screen (CS ticket created,
// payment confirmed, etc).
//
// W40.1 (2026-10-21, M3): `leading` renders a control BEFORE the Finish &
// Next button, the slot the approved 14 Customer & Addresses footer uses
// for "Save & Return" (`button#ca-save-return.cx-save-return` is the
// first child of footer.footer, MEASURED in 14-customer-addresses.
// manifest.json). Handoff decision 7 keeps Save & Return and Finish &
// Next distinct actions, so the screen owns the control; the footer only
// places it.
//
// TODO(runtime): dispatch to services/customer-routing-integration plus
// a priority-engine recompute for the next contact once the handler
// lands. Current server exposes POST /v1/actions/<NAME> + my-activity.
import type { ReactNode } from 'react';
import { Icon } from './IconSprite.js';

export interface ActionFooterProps {
  disabled: boolean;
  noteWhenDisabled: string;
  noteWhenEnabled: string;
  summaryStrong?: string;
  summaryBody?: string;
  onFinish?: () => void;
  /** W27: optional controls rendered beside the button (outcome picker). */
  controls?: ReactNode;
  /** W40.1: optional control rendered before Finish & Next (14: Save & Return). */
  leading?: ReactNode;
}

export function ActionFooter(props: ActionFooterProps): ReactNode {
  const {
    disabled,
    noteWhenDisabled,
    noteWhenEnabled,
    summaryStrong = 'One customer. A clear next step.',
    summaryBody = 'Your next step is retained with this customer.',
    onFinish,
    controls,
    leading,
  } = props;
  return (
    <footer className="footer" data-component="action-footer">
      {leading ?? null}
      <button
        data-approved-finish="true"
        id="complete"
        className="finish"
        data-action="finish-next"
        aria-describedby="finish-reason"
        disabled={disabled}
        type="button"
        onClick={onFinish}
      >
        Finish &amp; Next
        <Icon id="arrow" />
      </button>
      {controls ? <div className="footer-controls">{controls}</div> : null}
      <span id="finish-reason" className="footer-note">
        {disabled ? noteWhenDisabled : noteWhenEnabled}
      </span>
      <div className="footer-summary">
        <strong>{summaryStrong}</strong>
        <p>{summaryBody}</p>
      </div>
    </footer>
  );
}
